'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import type { MarketingPopoverSchema } from 'cms/schemaTypes/layout/marketingPopover'
import { usePathname } from 'next/navigation'

import { useOverlayStack } from 'shared/contexts/OverlayStackContext'
import { useFirstInteraction } from 'shared/hooks/useFirstInteraction'
import { isCheckoutPage } from 'shared/utils/routes'

import { useQueryMarketingPopover } from '../queries/useQueryMarketingPopover'
import {
  clearStaleViewCounters,
  isCapReached,
  markCapReached,
  readViewCount,
  registerView
} from '../storage'

// Long enough that the popover does not slot in between two modals closing and opening.
const SETTLE_DELAY_MS = 1000
const FALLBACK_DISPLAY_ID = 'default'

interface UseMarketingPopoverResult {
  isOpen: boolean
  data: MarketingPopoverSchema | null
  close: () => void
}

export const useMarketingPopover = (): UseMarketingPopoverResult => {
  const pathname = usePathname()
  const { stack } = useOverlayStack()

  const hasInteracted = useFirstInteraction()

  const [isPageVisible, setIsPageVisible] = useState(false)

  // Pessimistic until the cookie is read on the client, so the gate never opens
  // on the server render.
  const [isCapShortcutClear, setIsCapShortcutClear] = useState(false)
  const [isQuiet, setIsQuiet] = useState(false)
  const [isOpen, setIsOpen] = useState(false)

  const hasEvaluatedCap = useRef(false)

  const isSuppressedRoute = isCheckoutPage(pathname || '')

  useEffect(() => {
    setIsCapShortcutClear(!isCapReached())
  }, [])

  useEffect(() => {
    const sync = () =>
      setIsPageVisible(document.visibilityState === 'visible')

    sync()

    document.addEventListener('visibilitychange', sync)

    return () => document.removeEventListener('visibilitychange', sync)
  }, [])

  // The cleanup clears the timer whenever any gate flips back, so an overlay
  // opening mid-wait cancels the attempt instead of postponing it.
  useEffect(() => {
    if (
      !hasInteracted ||
      !isPageVisible ||
      !isCapShortcutClear ||
      isSuppressedRoute ||
      stack.length > 0
    ) {
      setIsQuiet(false)

      return
    }

    const timer = window.setTimeout(() => setIsQuiet(true), SETTLE_DELAY_MS)

    return () => window.clearTimeout(timer)
  }, [
    hasInteracted,
    isPageVisible,
    isCapShortcutClear,
    isSuppressedRoute,
    stack.length
  ])

  const { data } = useQueryMarketingPopover(isQuiet && !isSuppressedRoute)

  // Authoritative capping: only the payload knows which popover is live and how
  // many views it allows.
  useEffect(() => {
    if (hasEvaluatedCap.current || !data) return

    // The stack can fill up while the request is in flight. Hold the view back
    // rather than spending it on a popover behind a modal.
    if (!isQuiet) return

    hasEvaluatedCap.current = true

    const displayId = data.displayId || FALLBACK_DISPLAY_ID
    const displayLimit = data.displayLimit ?? 1

    clearStaleViewCounters(displayId)

    const viewCount = readViewCount(displayId)

    if (viewCount >= displayLimit) {
      markCapReached()

      return
    }

    registerView(displayId)

    if (viewCount + 1 >= displayLimit) {
      markCapReached()
    }

    setIsOpen(true)
  }, [data, isQuiet])

  // Checkout reached while the popover is up: the view is already counted, so
  // this only takes it off the screen.
  useEffect(() => {
    if (isOpen && isSuppressedRoute) {
      setIsOpen(false)
    }
  }, [isOpen, isSuppressedRoute])

  const close = useCallback(() => setIsOpen(false), [])

  return { isOpen, data: data ?? null, close }
}
